import Link from 'next/link'
import { noop } from 'lodash'
import { parseGqlErrors } from 'hooks/useQueryErrorHandler'
import { useRecoverPasswordMutation } from 'graphql/generated'
import { useControlledInput } from 'hooks'
import { FormEvent, useCallback } from 'react'
import Button from 'components/Button'
import Image from 'next/image'

interface IProps {
  email: string
}

const RecoverPassword: React.FC<IProps> = pageProps => {
  const [email, setEmail] = useControlledInput(pageProps.email)
  const [recover, { loading, error, data }] = useRecoverPasswordMutation({
    variables: {
      email
    },
    onError: noop
  })
  const success = !!data?.recoverPassword?.success
  const errors = parseGqlErrors(error)
  const errorMessage = errors['reset.password'] || errors._generic

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      recover()
    },
    [recover]
  )

  return (
    <div className="max-w-md w-full py-6 mt-5 px-6 bg-white rounded-lg shadow">
      <div>
        <Image
          className="mx-auto mt-3 h-8 w-auto"
          src="/static/new-logo-dark-horizontal@2x.png"
          alt="Upstream"
          width={121}
          height={32}
        />
        {success ? (
          <p className="my-4 w-full text-center">
            A link to reset your password has been sent to your inbox.
          </p>
        ) : (
          <h2 className="mt-6 text-center text-xl font-bold text-gray-900">
            Password reset
          </h2>
        )}
      </div>

      {!success && (
        <form onSubmit={onSubmit} className="text-center">
          <div className="rounded-md shadow-sm -space-y-px my-4">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                required
                autoFocus
                type="email"
                autoComplete="email"
                className="appearance-none relative block w-full px-3 py-3 border font-thin border-gray-300 placeholder-gray-500 text-gray-900 rounded-md mb-5 focus:outline-none focus:ring-blue-500 focus:border-blue focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={setEmail}
                disabled={loading}
              />
            </div>
          </div>

          {errorMessage && (
            <p className="text-red-500 text-center font-medium transition">
              {errorMessage}
            </p>
          )}

          <Button
            type="submit"
            label="Request password reset"
            color="blue"
            size="lg"
            rounded="lg"
            fullWidth
            loading={loading}
          />

          <div className="mt-5">
            <Link href="/auth/login" passHref>
              <Button
                color="link"
                size="md"
                label="Return to login"
                fullWidth
              />
            </Link>
          </div>
        </form>
      )}
    </div>
  )
}

export default RecoverPassword
